import omit from 'lodash/omit';
import { defineStore } from 'pinia';

import { GatewayApiClient, getHttpStatus } from '@evidentid/gateway-api-client';
import Logger from '@evidentid/logger';
import Severity from '@evidentid/logger/Severity';
import {
    ExceptionInput,
    ForceSubmissionConflictReason,
    OpenVerification,
} from '@evidentid/tprm-portal-lib/models/entity-details';
import { CollateralEntity } from '@evidentid/tprm-portal-lib/models/entity-details/Collateral/CollateralEntity.model';
import { CollateralMappingOptions } from '@evidentid/tprm-portal-lib/models/entity-details/Collateral/CollateralMappingOptions.model';
import {
    EntityRequirementDetails,
} from '@evidentid/tprm-portal-lib/models/entity-details/EntityRequirement.model';
import {
    VerificationRequest,
} from '@evidentid/tprm-portal-lib/models/notification-configuration/VerificationRequest.model';
import Tracker from '@evidentid/tracker';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

import {
    transformCollateralsDetails,
} from '@/modules/entity-details/utils/transformCollateralsDetails/transformCollateralsDetails';

export interface EntityDetailsRequirement {
    gatewayClient: GatewayApiClient;
    tracker?: Tracker<any>;
    logger?: Logger<any>;
}

// Actions reach the Application through the $app Pinia plugin; typed here once.
function appOf(store: { $app: any }): EntityDetailsRequirement {
    return store.$app;
}

// The legacy module fired these through app.observer.onMutation() hooks in
// its module-builder registration; inlined at the same transition points.
function trackDetailsEvent(
    app: EntityDetailsRequirement,
    event: { name: string, category: string, phase: string, subject: string },
    rpName: string,
): void {
    const { name, category, phase, subject } = event;
    const message = `[${phase}] ${subject} for ${rpName}`;
    app.tracker?.track({
        name: phase === 'Failed' ? `${name} Failed` : name,
        description: message,
        rpName,
    });
    app.logger?.addBreadcrumb({
        ...(phase === 'Failed' ? { level: Severity.error } : {}),
        category,
        message,
        data: { rpName },
    });
}

export interface RequirementDetailsListStatus {
    status: OperationStatus;
    list: EntityRequirementDetails[];
}

export interface OpenVerificationStatus {
    status: OperationStatus;
    // The entity the verification belongs to, so a reload for another entity never shows this one's.
    entityId: string | null;
    openVerification: OpenVerification | null;
}

export interface ForceSubmissionStatus {
    status: OperationStatus;
    // The API refused because the verification changed since it was loaded.
    conflict: boolean;
    conflictReason: ForceSubmissionConflictReason | null;
}

interface EntityDetailsState {
    requirementDetailsListStatus: Record<string, RequirementDetailsListStatus>;
    collateralEntitiesStatus: Record<string, { status: OperationStatus, list: CollateralEntity[] }>;
    collateralMappingOptionsStatus:
    Record<string, { status: OperationStatus, list: CollateralMappingOptions[] }>;
    verificationRequestsStatus:
    Record<string, { status: OperationStatus, list: VerificationRequest[] }>;
    patchCollateralStatus: Record<string, OperationStatus>;
    grantExceptionStatus: Record<string, OperationStatus>;
    removeExceptionStatus: Record<string, OperationStatus>;
    openVerificationStatus: Record<string, OpenVerificationStatus>;
    forceSubmissionStatus: Record<string, ForceSubmissionStatus>;
}

export const useEntityDetailsStore = defineStore('entityDetails', {
    state: (): EntityDetailsState => ({
        requirementDetailsListStatus: {},
        collateralEntitiesStatus: {},
        collateralMappingOptionsStatus: {},
        verificationRequestsStatus: {},
        patchCollateralStatus: {},
        grantExceptionStatus: {},
        removeExceptionStatus: {},
        openVerificationStatus: {},
        forceSubmissionStatus: {},
    }),
    actions: {

        startLoadingRequirementDetailsList(payload: { rpName: string }) {
            trackDetailsEvent(this.$app as EntityDetailsRequirement,
                { name: 'Load Requirement Details', category: 'Entity Details - Requirement Details', phase: 'Requested', subject: 'Load requirement details' },
                payload.rpName);
            this.requirementDetailsListStatus = {
                ...this.requirementDetailsListStatus,
                [payload.rpName]: {
                    status: OperationStatus.loading,
                    list: this.requirementDetailsListStatus[payload.rpName]?.list || [],
                },
            };
        },
        failLoadingRequirementDetailsList(payload: { rpName: string }) {
            trackDetailsEvent(this.$app as EntityDetailsRequirement,
                { name: 'Load Requirement Details', category: 'Entity Details - Requirement Details', phase: 'Failed', subject: 'Load requirement details' },
                payload.rpName);
            this.requirementDetailsListStatus = {
                ...this.requirementDetailsListStatus,
                [payload.rpName]: {
                    status: OperationStatus.error,
                    list: this.requirementDetailsListStatus[payload.rpName]?.list || [],
                },
            };
        },
        finishLoadingRequirementDetailsList(payload: {
            rpName: string;
            list: EntityRequirementDetails[];
        }) {
            trackDetailsEvent(this.$app as EntityDetailsRequirement,
                { name: 'Load Requirement Details', category: 'Entity Details - Requirement Details', phase: 'Finished', subject: 'Load requirement details' },
                payload.rpName);
            payload.list.forEach((requirementDetails) => {
                if (!requirementDetails.coverage) {
                    return;
                }

                let collateral = requirementDetails.coverage.details?.collateral;

                if (collateral) {
                    collateral = transformCollateralsDetails(collateral as any);
                    requirementDetails.coverage.details.collateral = collateral;
                }
            });

            this.requirementDetailsListStatus = {
                ...this.requirementDetailsListStatus,
                [payload.rpName]: {
                    status: OperationStatus.success,
                    list: payload.list,
                },
            };
        },

        startLoadingCollateralEntities(payload: { rpName: string }) {
            trackDetailsEvent(this.$app as EntityDetailsRequirement,
                { name: 'Load Collateral Entities', category: 'Entity Details - Collateral Entities', phase: 'Requested', subject: 'Load collateral entities' },
                payload.rpName);
            this.collateralEntitiesStatus = {
                ...this.collateralEntitiesStatus,
                [payload.rpName]: {
                    status: OperationStatus.loading,
                    list: this.collateralEntitiesStatus[payload.rpName]?.list || [],
                },
            };
        },
        failLoadingCollateralEntities(payload: { rpName: string }) {
            trackDetailsEvent(this.$app as EntityDetailsRequirement,
                { name: 'Load Collateral Entities', category: 'Entity Details - Collateral Entities', phase: 'Failed', subject: 'Load collateral entities' },
                payload.rpName);
            this.collateralEntitiesStatus = {
                ...this.collateralEntitiesStatus,
                [payload.rpName]: {
                    status: OperationStatus.error,
                    list: this.collateralEntitiesStatus[payload.rpName]?.list || [],
                },
            };
        },

        finishLoadingCollateralEntities(payload: {
            rpName: string;
            list: CollateralEntity[];
        }) {
            trackDetailsEvent(this.$app as EntityDetailsRequirement,
                { name: 'Load Collateral Entities', category: 'Entity Details - Collateral Entities', phase: 'Finished', subject: 'Load collateral entities' },
                payload.rpName);
            this.collateralEntitiesStatus = {
                ...this.collateralEntitiesStatus,
                [payload.rpName]: {
                    status: OperationStatus.success,
                    list: payload.list,
                },
            };
        },
        startLoadingCollateralMappingOptions(payload: { rpName: string }) {
            this.collateralMappingOptionsStatus = {
                ...this.collateralMappingOptionsStatus,
                [payload.rpName]: {
                    status: OperationStatus.loading,
                    list: this.collateralMappingOptionsStatus[payload.rpName]?.list || [],
                },
            };
        },
        failLoadingCollateralMappingOptions(payload: { rpName: string }) {
            this.collateralMappingOptionsStatus = {
                ...this.collateralMappingOptionsStatus,
                [payload.rpName]: {
                    status: OperationStatus.error,
                    list: this.collateralMappingOptionsStatus[payload.rpName]?.list || [],
                },
            };
        },

        finishLoadingCollateralMappingOptions(payload: {
            rpName: string;
            list: CollateralMappingOptions[];
        }) {
            this.collateralMappingOptionsStatus = {
                ...this.collateralMappingOptionsStatus,
                [payload.rpName]: {
                    status: OperationStatus.success,
                    list: payload.list,
                },
            };
        },
        startLoadingVerificationRequests(payload: { rpName: string }) {
            this.verificationRequestsStatus = {
                ...this.verificationRequestsStatus,
                [payload.rpName]: {
                    status: OperationStatus.loading,
                    list: this.verificationRequestsStatus[payload.rpName]?.list || [],
                },
            };
        },
        failLoadingVerificationRequests(payload: { rpName: string }) {
            this.verificationRequestsStatus = {
                ...this.verificationRequestsStatus,
                [payload.rpName]: {
                    status: OperationStatus.error,
                    list: this.verificationRequestsStatus[payload.rpName]?.list || [],
                },
            };
        },
        finishLoadingVerificationRequests(payload: {
            rpName: string;
            list: VerificationRequest[];
        }) {
            this.verificationRequestsStatus = {
                ...this.verificationRequestsStatus,
                [payload.rpName]: {
                    status: OperationStatus.success,
                    list: payload.list,
                },
            };
        },

        startPatchingCollaterals(payload: { rpName: string }) {
            this.patchCollateralStatus = {
                ...this.patchCollateralStatus,
                [payload.rpName]: OperationStatus.loading,
            };
        },
        failPatchingCollaterals(payload: { rpName: string }) {
            this.patchCollateralStatus = {
                ...this.patchCollateralStatus,
                [payload.rpName]: OperationStatus.error,
            };
        },
        finishPatchingCollaterals(payload: {
            rpName: string;
        }) {
            this.patchCollateralStatus = {
                ...this.patchCollateralStatus,
                [payload.rpName]: OperationStatus.success,
            };
        },
        startGrantingExceptions(payload: { rpName: string }) {
            this.grantExceptionStatus = {
                ...this.grantExceptionStatus,
                [payload.rpName]: OperationStatus.loading,
            };
        },
        failGrantingExceptions(payload: { rpName: string }) {
            this.grantExceptionStatus = {
                ...this.grantExceptionStatus,
                [payload.rpName]: OperationStatus.error,
            };
        },
        finishGrantingExceptions(payload: {
            rpName: string;
        }) {
            this.grantExceptionStatus = {
                ...this.grantExceptionStatus,
                [payload.rpName]: OperationStatus.success,
            };
        },

        startRemovingExceptions(payload: { rpName: string }) {
            this.removeExceptionStatus = {
                ...this.removeExceptionStatus,
                [payload.rpName]: OperationStatus.loading,
            };
        },
        failRemovingExceptions(payload: { rpName: string }) {
            this.removeExceptionStatus = {
                ...this.removeExceptionStatus,
                [payload.rpName]: OperationStatus.error,
            };
        },
        finishRemovingExceptions(payload: {
            rpName: string;
        }) {
            this.removeExceptionStatus = {
                ...this.removeExceptionStatus,
                [payload.rpName]: OperationStatus.success,
            };
        },

        async loadRequirementDetailsList(payload: {
            rpName: string; entityId: string;
        }) {
            const { rpName, entityId } = payload;
            try {
                this.startLoadingRequirementDetailsList({ rpName });
                const list = await appOf(this).gatewayClient.tprmUi().getTprmRequirementsDetailsList(rpName, entityId);
                this.finishLoadingRequirementDetailsList({ rpName, list });
            } catch (error) {
                console.warn('load requirement details error', error);
                this.failLoadingRequirementDetailsList({ rpName });
            }
        },
        clearRequirementDetailsList(payload: { rpName: string }) {
            const rpName = payload.rpName;
            this.requirementDetailsListStatus = omit(this.requirementDetailsListStatus, [ rpName ]);
        },
        async loadCollateralEntities(payload: {
            rpName: string; entityId: string;
        }) {
            const { rpName, entityId } = payload;
            try {
                this.startLoadingCollateralEntities({ rpName });
                const list = await appOf(this).gatewayClient.tprmUi().getTprmCollaterals(rpName, entityId);
                this.finishLoadingCollateralEntities({ rpName, list });
            } catch (error) {
                console.warn('load collateral entities error', error);
                this.failLoadingCollateralEntities({ rpName });
            }
        },
        async loadCollateralMappingOptions(payload: {
            rpName: string; entityId: string;
        }) {
            const { rpName, entityId } = payload;
            try {
                this.startLoadingCollateralMappingOptions({ rpName });
                const list = await appOf(this).gatewayClient.tprmUi().getTprmCollateralMappingOptions(rpName, entityId);
                this.finishLoadingCollateralMappingOptions({ rpName, list });
            } catch (error) {
                console.warn('load collateral mapping options error', error);
                this.failLoadingCollateralMappingOptions({ rpName });
            }
        },
        async loadVerificationRequests(payload: {
            rpName: string; entityId: string;
        }) {
            const { rpName, entityId } = payload;
            try {
                this.startLoadingVerificationRequests({ rpName });
                const list = await appOf(this).gatewayClient.tprmUi().getTprmEntityVerifications(rpName, entityId);
                this.finishLoadingVerificationRequests({ rpName, list });
            } catch (error) {
                console.warn('load collateral mapping options error', error);
                this.failLoadingVerificationRequests({ rpName });
            }
        },
        clearVerificationRequests(payload: { rpName: string }) {
            const rpName = payload.rpName;
            this.verificationRequestsStatus = omit(this.verificationRequestsStatus, [ rpName ]);
        },
        clearCollateralMappingOptions(payload: { rpName: string }) {
            const rpName = payload.rpName;
            this.collateralMappingOptionsStatus = omit(this.collateralMappingOptionsStatus, [ rpName ]);
        },
        clearCollateralEntities(payload: { rpName: string }) {
            const rpName = payload.rpName;
            this.collateralEntitiesStatus = omit(this.collateralEntitiesStatus, [ rpName ]);
        },
        async patchCollaterals(payload: {
            rpName: string;
            entityId: string;
            collateralPatchObjects: Partial<CollateralEntity>[];
        }) {
            const { rpName, entityId, collateralPatchObjects } = payload;
            try {
                this.startPatchingCollaterals({ rpName });
                await appOf(this).gatewayClient.tprmUi().patchTprmCollaterals(rpName, entityId, collateralPatchObjects);
                this.finishPatchingCollaterals({ rpName });
            } catch (error) {
                this.failPatchingCollaterals({ rpName });
                console.error(error);
            }
        },
        clearPatchCollateralsStatus(payload: { rpName: string }) {
            const rpName = payload.rpName;
            this.patchCollateralStatus = omit(this.patchCollateralStatus, [ rpName ]);
        },
        async grantExceptions(payload: {
            rpName: string;
            entityId: string;
            exceptions: ExceptionInput[];
        }) {
            const { rpName, entityId, exceptions } = payload;
            try {
                this.startGrantingExceptions({ rpName });
                await appOf(this).gatewayClient.tprmUi().grantException(rpName, entityId, exceptions);
                this.finishGrantingExceptions({ rpName });
            } catch (error) {
                this.failGrantingExceptions({ rpName });
            }
        },
        clearGrantExceptionsStatus(payload: { rpName: string }) {
            const rpName = payload.rpName;
            this.grantExceptionStatus = omit(this.grantExceptionStatus, [ rpName ]);
        },

        async removeException(payload: {
            rpName: string;
            entityId: string;
            exceptionIds: string[];
            note?: string;
        }) {
            const { rpName, entityId, exceptionIds, note } = payload;
            try {
                this.startRemovingExceptions({ rpName });
                await appOf(this).gatewayClient.tprmUi().removeExceptions(
                    rpName,
                    entityId,
                    exceptionIds.map((id) => ({ id, note: note || undefined })),
                );
                this.finishRemovingExceptions({ rpName });
            } catch (error) {
                this.failRemovingExceptions({ rpName });
            }
        },
        clearRemoveExceptionsStatus(payload: { rpName: string }) {
            const rpName = payload.rpName;
            this.removeExceptionStatus = omit(this.removeExceptionStatus, [ rpName ]);
        },

        async loadOpenVerification(payload: { rpName: string, entityId: string }) {
            const { rpName, entityId } = payload;
            const current = this.openVerificationStatus[rpName];
            this.openVerificationStatus = {
                ...this.openVerificationStatus,
                [rpName]: {
                    status: OperationStatus.loading,
                    entityId,
                    openVerification: current?.entityId === entityId ? current.openVerification : null,
                },
            };
            // A response for an entity the user has since left must not replace the one now being shown.
            const isStillCurrent = () => this.openVerificationStatus[rpName]?.entityId === entityId;
            try {
                const openVerification = await appOf(this).gatewayClient.tprmUi()
                    .getOpenVerification(rpName, entityId);
                if (isStillCurrent()) {
                    this.openVerificationStatus = {
                        ...this.openVerificationStatus,
                        [rpName]: { status: OperationStatus.success, entityId, openVerification },
                    };
                }
            } catch (error) {
                console.warn('load open verification error', error);
                if (isStillCurrent()) {
                    this.openVerificationStatus = {
                        ...this.openVerificationStatus,
                        [rpName]: { status: OperationStatus.error, entityId, openVerification: null },
                    };
                }
            }
        },
        clearOpenVerification(payload: { rpName: string }) {
            this.openVerificationStatus = omit(this.openVerificationStatus, [ payload.rpName ]);
        },

        async forceSubmission(payload: { rpName: string, entityId: string, verificationId: string }) {
            const { rpName, entityId, verificationId } = payload;
            this.forceSubmissionStatus = {
                ...this.forceSubmissionStatus,
                [rpName]: { status: OperationStatus.loading, conflict: false, conflictReason: null },
            };
            try {
                await appOf(this).gatewayClient.tprmUi().forceSubmission(rpName, entityId, verificationId);
                // Hide the banner now rather than when the reload lands, so it can't be forced twice.
                const shown = this.openVerificationStatus[rpName];
                if (shown?.openVerification?.verificationId === verificationId) {
                    this.openVerificationStatus = {
                        ...this.openVerificationStatus,
                        [rpName]: { ...shown, openVerification: null },
                    };
                }
                this.forceSubmissionStatus = {
                    ...this.forceSubmissionStatus,
                    [rpName]: { status: OperationStatus.success, conflict: false, conflictReason: null },
                };
            } catch (error: any) {
                console.warn('force submission error', error);
                const conflict = getHttpStatus(error) === 409;
                this.forceSubmissionStatus = {
                    ...this.forceSubmissionStatus,
                    [rpName]: {
                        status: OperationStatus.error,
                        conflict,
                        conflictReason: conflict ? (error?.reason ?? null) : null,
                    },
                };
            }
        },
        clearForceSubmissionStatus(payload: { rpName: string }) {
            this.forceSubmissionStatus = omit(this.forceSubmissionStatus, [ payload.rpName ]);
        },
    },
});
